import React, { useMemo, useCallback } from 'react';
import { Text, Heading, Box, Select, SelectOptionProps, VStack, HStack } from '@televet/kibble-ui';
import { useSurveySettingsContext, ISurveyGeneralSettings } from 'pages/Survey/context/SurveySettingsContext';
import { SurveyType } from 'shared/types/graphql';

enum ConversionTypes {
  toMonths = 'toMonths',
  toDays = 'toDays',
}

interface SurveyFrequencyProps {
  handleChange: (field: keyof ISurveyGeneralSettings, newValue: number) => void;
}

const SurveyFrequency = ({ handleChange }: SurveyFrequencyProps): JSX.Element => {
  const {
    generalSettings: { frequencyInDays },
    surveyType,
  } = useSurveySettingsContext();

  const convertFrequencyOptions = useCallback(({ value, conversionType }): number => {
    switch (conversionType) {
      case ConversionTypes.toMonths:
        return Math.floor(value / 30);
      case ConversionTypes.toDays:
        return value * 30;
      default:
        return 0;
    }
  }, []);

  const frequencyOptions = useMemo((): SelectOptionProps[] => {
    const options: SelectOptionProps[] = [];
    let frequencyDelay = 1;
    while (frequencyDelay <= 12) {
      options.push({
        value: `${convertFrequencyOptions({ value: frequencyDelay, conversionType: ConversionTypes.toDays })}`,
        label: `${frequencyDelay}`,
      });
      frequencyDelay++;
    }
    return options || [];
  }, [convertFrequencyOptions]);

  const handleFrequencyAmountChange = useCallback(
    (selectedItem: SelectOptionProps) => {
      if (selectedItem?.value) {
        handleChange('frequencyInDays', parseInt(selectedItem?.value));
      }
    },
    [handleChange],
  );

  return (
    <Box className="settings__container" mt="40px">
      <Heading className="settings__header" size="md" mb={2}>
        Send Frequency
      </Heading>
      <VStack alignItems="flex-start">
        <Text className="settings__description" variant="subtle" mb={4}>
          Limit how often a recipient receives a{surveyType === SurveyType.Nps && 'n'} {surveyType} survey
        </Text>
        <HStack mt={`0 !important`}>
          <Text display="inline-block" size="md" className="settings__fieldRow-label">
            Once every
          </Text>
          <Box m="0 10px" display="inline-block" width="70px">
            <Select
              value={`${frequencyInDays}`}
              listProps={{
                onSelect: handleFrequencyAmountChange,
                maxH: '200px',
              }}
              options={frequencyOptions}
            />
          </Box>
          <Text display="inline-block" size="md" className="settings__fieldRow-label">
            month(s)
          </Text>
        </HStack>
      </VStack>
    </Box>
  );
};

export default SurveyFrequency;
